import { Pathway } from 'src/api/pathways/types';
import { ONE_DAY_IN_MILLISECONDS } from 'src/constants/times.const';

/**
 * Learners who have been away at least this long get the returning-learner
 * treatment on Home — in practice, anyone whose last completed path step was on
 * an earlier calendar day. Someone who has already worked on their path today
 * sees nothing.
 */
export const RETURNING_LEARNER_DAYS = 1;

/**
 * Normalised to local midday, matching `buildSessionsTrackData`: comparing noons
 * rather than midnights keeps a DST shift from moving a date across a day boundary.
 */
const localMidday = (date: Date): number => {
  const normalized = new Date(date);
  normalized.setHours(12, 0, 0, 0);
  return normalized.getTime();
};

/**
 * Most recent completion anywhere on the pathway — sessions and checkpoints alike.
 * Returns null for a pathway with nothing completed yet.
 */
export const getLastPathSessionDate = (pathway: Pathway): Date | null => {
  let latest: number | null = null;

  const consider = (stamp: string | null) => {
    if (!stamp) return;
    const time = Date.parse(stamp);
    if (Number.isNaN(time)) return;
    if (latest === null || time > latest) {
      latest = time;
    }
  };

  pathway.checkpoints.forEach(checkpoint => {
    consider(checkpoint.completedAt);
    checkpoint.nodes.forEach(node => consider(node.completedAt));
  });

  return latest === null ? null : new Date(latest);
};

/**
 * Whole calendar days between the learner's last completed path step and `now`.
 * Null when they have never completed anything, since "away for N days" is not
 * meaningful for someone who has not started.
 */
export const getDaysSinceLastPathSession = (pathway: Pathway, now: Date): number | null => {
  const last = getLastPathSessionDate(pathway);
  if (!last) return null;

  const elapsed = localMidday(now) - localMidday(last);
  return Math.max(0, Math.round(elapsed / ONE_DAY_IN_MILLISECONDS));
};
